import React, { useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Instagram, Linkedin, Send } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

const FAQ = [
  { q: "Quanto tempo prima di ricevere risposta?", a: "Cerco di rispondere il prima possibile, ma i tempi possono variare. Se non ricevi risposta entro una settimana, puoi ricontattarmi via Telegram a @danpiras_psy (+39 375 614 8885 — solo Telegram). Controlla anche la cartella spam." },
  { q: "Posso scrivere in modo anonimo?", a: "Sì, puoi usare un indirizzo email non collegato al tuo nome. Per la prima consulenza sarà necessario condividere l'identità." },
  { q: "Come vengono trattati i miei dati?", a: "In conformità al GDPR. Non li cedo a terzi. Dettagli completi nella Privacy Policy." },
  { q: "Qual è la differenza tra psicologə e psicoterapeuta?", a: "Sono psicologə iscritto all'Albo (n. 22141) e consulente in sessuologia. La consulenza psicologica è un percorso mirato di ascolto, esplorazione e sostegno. La psicoterapia richiede una specializzazione post-lauream aggiuntiva e ha finalità e cornice diverse: se emerge il bisogno di un lavoro psicoterapeutico strutturato, te lo segnalo e ti oriento verso colleghə con quella specializzazione." },
];

export default function Contatti() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "", reason: "info", gdpr_consent: false });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.gdpr_consent) { toast.error("Devi accettare l'informativa GDPR"); return; }
    setLoading(true);
    try {
      await http.post("/contact", form);
      toast.success("Messaggio inviato. Se non ricevi risposta entro una settimana, scrivimi su Telegram (@danpiras_psy).");
      setForm({ name: "", email: "", subject: "", message: "", reason: "info", gdpr_consent: false });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore, riprova");
    } finally { setLoading(false); }
  };

  return (
    <div className="container-editorial py-16 lg:py-24" data-testid="page-contatti">
      <div className="grid lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <span className="pill">Contatti</span>
          <h1 className="mt-4 font-display text-5xl sm:text-6xl tracking-tighter leading-[0.95]">Scrivimi.<br />Se non rispondo,<br /><span className="italic font-normal text-fucsia">provaci su Telegram.</span></h1>
          <p className="mt-6 text-lg text-viola/75">Compila il modulo o usa uno dei canali qui sotto. Se non ricevi risposta entro una settimana, contattami su Telegram: risponderò appena possibile.</p>

          <div className="mt-10 space-y-4">
            <a href="mailto:dania.piras90@gmail.com" className="flex items-center gap-3 text-viola/85 hover:text-fucsia" data-testid="contact-email"><Mail size={16} /> dania.piras90@gmail.com</a>
            <a href="mailto:danpiras.psy@gmail.com" className="flex items-center gap-3 text-viola/85 hover:text-fucsia" data-testid="contact-email-2"><Mail size={16} /> danpiras.psy@gmail.com</a>
            <a href="https://t.me/danpiras_psy" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-viola/85 hover:text-fucsia" data-testid="contact-telegram">
              <Send size={16} /> Telegram · @danpiras_psy · +39 375 614 8885
            </a>
            <p className="text-xs text-viola/50 pl-7">Il numero è attivo <strong>solo su Telegram</strong> — non chiamate, non SMS.</p>
            <a href="https://www.instagram.com/hello_policose/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-viola/85 hover:text-fucsia" data-testid="contact-instagram"><Instagram size={16} /> @hello_policose</a>
            <a href="https://linktr.ee/policose" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-viola/85 hover:text-fucsia" data-testid="contact-linkedin"><Linkedin size={16} /> linktr.ee/policose</a>
          </div>

          <div className="mt-12">
            <h2 className="font-display text-2xl">FAQ</h2>
            <Accordion type="single" collapsible className="mt-4">
              {FAQ.map((f, i) => (
                <AccordionItem key={f.q} value={`c-${i}`} className="border-viola/10">
                  <AccordionTrigger className="text-left font-medium hover:text-fucsia" data-testid={`contact-faq-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-viola/75">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-7 rounded-2xl bg-lilla/40 border border-viola/10 p-8 md:p-10 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-viola/70">Nome</span>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 w-full rounded-lg border border-viola/20 bg-cream px-4 py-3 focus:outline-none focus:ring-2 focus:ring-fucsia" data-testid="contact-name" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-widest text-viola/70">Email</span>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 w-full rounded-lg border border-viola/20 bg-cream px-4 py-3 focus:outline-none focus:ring-2 focus:ring-fucsia" data-testid="contact-email-input" />
            </label>
          </div>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-viola/70">Motivo</span>
            <select value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="mt-2 w-full rounded-lg border border-viola/20 bg-cream px-4 py-3 focus:outline-none focus:ring-2 focus:ring-fucsia" data-testid="contact-reason">
              <option value="info">Informazioni</option>
              <option value="consulenza">Consulenza clinica</option>
              <option value="supervisione">Supervisione</option>
              <option value="media">Media / interviste</option>
              <option value="professionale">Consulenza professionale</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-viola/70">Oggetto</span>
            <input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2 w-full rounded-lg border border-viola/20 bg-cream px-4 py-3 focus:outline-none focus:ring-2 focus:ring-fucsia" data-testid="contact-subject" />
          </label>
          <label className="block">
            <span className="text-xs font-semibold uppercase tracking-widest text-viola/70">Messaggio</span>
            <textarea required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-2 w-full rounded-lg border border-viola/20 bg-cream px-4 py-3 focus:outline-none focus:ring-2 focus:ring-fucsia" data-testid="contact-message" />
          </label>
          <label className="flex items-start gap-3 text-sm text-viola/80">
            <input type="checkbox" checked={form.gdpr_consent} onChange={(e) => setForm({ ...form, gdpr_consent: e.target.checked })} className="mt-1" data-testid="contact-gdpr" />
            <span>Ho letto l'informativa sulla privacy e acconsento al trattamento dei dati personali secondo l'art. 13 GDPR (Reg. UE 2016/679).</span>
          </label>
          <button type="submit" disabled={loading} className="btn-primary" data-testid="contact-submit">
            {loading ? "Invio…" : "Invia messaggio"}
          </button>
        </form>
      </div>
    </div>
  );
}
